import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUrlTableState from "../../../hooks/useUrlTableState";
import {
  Plus,
  Target,
  Edit,
  Trash2,
  Eye,
  Search,
  Download,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  ChevronDown,
  X,
  FileText,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { goalApi } from "../../../api/goalApi";
import { toast } from "../../../utils/toast";
import { useEmployees } from "../../../hooks/useEmployees";
import { usePerformanceCycles } from "../../../hooks/usePerformanceCycles";
import WarningPopup from "../../../components/WarningPopup";
import { formatDate } from "../../../utils/stringUtils";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../../utils/formValidation";

const GoalManagement = ({ initialMode, id: propId, onClose, isDrawer }) => {
  // Updated: Fixed export buttons and popup issues - 2025-10-08
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchInputRef = useRef(null);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    cycle: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: tableFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateTableFilter,
    updatePage: updateTablePage,
    updateLimit: updateTableLimit,
    resetFilters: resetAllFilters
  } = useUrlTableState(defaultFilters);

  const searchInput = searchQuery;
  const setSearchInput = setSearchQuery;
  const debouncedSearchTerm = tableFilters.search || '';

  const filterStatus = tableFilters.status || 'all';
  const setFilterStatus = (status) => updateTableFilter({ status });

  const filterCycle = tableFilters.cycle || 'all';
  const setFilterCycle = (cycle) => updateTableFilter({ cycle });

  const filterStartDate = tableFilters.startDate || '';
  const setFilterStartDate = (startDate) => updateTableFilter({ startDate });
  const filterEndDate = tableFilters.endDate || '';
  const setFilterEndDate = (endDate) => updateTableFilter({ endDate });

  const page = tableFilters.page;
  const setPage = (page) => updateTablePage(page);

  const limit = tableFilters.limit;
  const setLimit = (limit) => updateTableLimit(limit);

  const closeModals = () => {
    setShowCreateModal(false);
    setShowViewModal(false);
    setNewGoal(initialNewGoal);
    setCreateErrors({});
    setTouched({});
    if (isDrawer && onClose) {
      onClose();
    }
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const [selectedGoalsMap, setSelectedGoalsMap] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Memoize filters for React Query
  const goalFilters = useMemo(
    () => ({
      status: filterStatus !== "all" ? filterStatus : undefined,
      // type: filterType !== "all" ? filterType : undefined,
      cycle: filterCycle !== "all" ? filterCycle : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
      q: debouncedSearchTerm?.trim() || undefined,
      page,
      limit
    }),
    [filterStatus, filterCycle, filterStartDate, filterEndDate, debouncedSearchTerm, page, limit]
  );

  // React Query: Fetch goals
  const {
    data: goalsResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['goals', goalFilters],
    queryFn: async () => {
      const response = await goalApi.getGoals(goalFilters);
      return response;
    },
    placeholderData: keepPreviousData,
  });

  // Fetch all employees for the dropdown
  const {
    data: employeesResponse,
    isLoading: employeesLoading,
    isError: employeesError,
  } = useEmployees();

  // React Query: Create goal mutation
  const createGoalMutation = useMutation({
    mutationFn: (goalData) => goalApi.createGoal(goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-stats'] });
      toast.success("Goal created successfully!");
      closeModals();
    },
    onError: (error) => {
      console.error("Error creating goal:", error);
      toast.error("Failed to create goal. Please try again.");
    },
  });

  // React Query: Update goal mutation
  const updateGoalMutation = useMutation({
    mutationFn: ({ goalId, goalData }) => goalApi.updateGoal(goalId, goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-stats'] });
      toast.success("Goal updated successfully!");
      closeModals();
    },
    onError: (error) => {
      console.error("Error updating goal:", error);
      toast.error("Failed to update goal. Please try again.");
    },
  });

  // React Query: Delete goal mutation
  const deleteGoalMutation = useMutation({
    mutationFn: (goalId) => goalApi.deleteGoal(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-stats'] });
      setShowDeleteWarning(false);
      setGoalToDelete(null);
    },
    onError: (error) => {
      console.error("Error deleting goal:", error);
      toast.error("Failed to delete goal. Please try again.");
    },
  });

  // Get goals data and pagination from API response
  const goals = goalsResponse?.data || [];
  const pagination = goalsResponse?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  };

  // Server-side filtering is applied; client simply uses returned goals
  const filteredGoals = goals;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedGoals = useMemo(() => {
    if (!filteredGoals) return [];
    if (!sortField) return filteredGoals;

    return [...filteredGoals].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'employee':
          aValue = (a.employee || '').toLowerCase();
          bValue = (b.employee || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'progress':
          aValue = Number(a.progress || 0);
          bValue = Number(b.progress || 0);
          break;
        case 'priority':
          // Custom priority order: high > medium > low
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'endDate':
          aValue = new Date(a.endDate || 0).getTime();
          bValue = new Date(b.endDate || 0).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredGoals, sortField, sortOrder]);

  // Keep using usePerformanceCycles hook for cycles (it's complex and used elsewhere)
  // Exclude closed cycles for goal creation/editing
  const {
    data: cyclesResponse,
    isLoading: cyclesLoading,
    isError: cyclesError,
  } = usePerformanceCycles({ excludeClosed: true, status: "launched" });

  // Get cycles data from API response
  const cycles =
    cyclesResponse?.data?.cycles ||
    cyclesResponse?.cycles ||
    cyclesResponse?.data ||
    cyclesResponse ||
    [];

  // Get employees data from API response
  const employees =
    employeesResponse?.data?.employees ||
    employeesResponse?.employees ||
    employeesResponse?.data ||
    employeesResponse ||
    [];

  // For now, include all employees as potential managers since we don't have proper role data
  // In a real system, you would filter based on actual role/position data
  const managers = employees;

  // Handle initial mode (create/view/edit) - must be after goals is defined
  useEffect(() => {
    if (initialMode === 'create') {
      setNewGoal(initialNewGoal);
      setShowCreateModal(true);
    } else if ((initialMode === 'view' || initialMode === 'edit') && propId) {
      // Find goal in the current list
      const goal = goals.find((g) => g.id === propId || g._id === propId);
      if (goal) {
        if (initialMode === 'view') {
          handleViewGoal(propId);
        } else {
          handleEditGoal(propId);
        }
      } else if (!isLoading) {
        // If not found and not loading, we might need to fetch it specifically or it doesn't exist

      }
    }
  }, [initialMode, propId, goals.length, isLoading]);


  const {
    data: goalStatsResponse,
    isLoading: statsLoading,
    isError: statsError,
  } = useQuery({
    queryKey: ['goal-stats'],
    queryFn: async () => {
      const response = await goalApi.getGoalStats();
      return response.data || response || {};
    },
  });

  const goalStats =
    goalStatsResponse?.data?.stats ||
    goalStatsResponse?.stats ||
    goalStatsResponse || {
      totalGoals: 0,
      inProgress: 0,
      completed: 0,
      avgProgress: 0,
    };

  const normalizedGoalStats = {
    totalGoals: goalStats.totalGoals ?? 0,
    inProgress: goalStats.inProgress ?? 0,
    completed: goalStats.completed ?? 0,
    avgProgress: Math.round(goalStats.avgProgress ?? 0),
  };

  const renderStatValue = (value, suffix = "") => {
    if (statsLoading) {
      return (
        <span className="inline-flex h-6 w-12 rounded bg-gray-200 animate-pulse" />
      );
    }

    if (statsError) {
      return <span className="text-sm text-gray-500">--</span>;
    }

    return (
      <>
        {formatIndianNumber(value)}
        {suffix}
      </>
    );
  };

  const initialNewGoal = {
    title: "",
    description: "",
    employee: "",
    employeeId: "",
    manager: "",
    managerId: "",
    status: "not_started",
    progress: 0,
    startDate: "",
    endDate: "",
    priority: "medium",
    cycle: "",
    lastUpdated: new Date().toISOString().split("T")[0],
    remark: "",
  };
  const [newGoal, setNewGoal] = useState(initialNewGoal);
  const [createErrors, setCreateErrors] = useState({});

  // Function to calculate status based on progress and endDate
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    let error = undefined;
    switch (name) {
      case 'title':
        if (!value?.trim()) return "Title is required";
        if (value.length > 80) return "Title must be ≤ 80 characters";
        if (!VALID_TEXT_REGEX.test(value)) return "Title contains invalid characters";
        if (value.trim().length < 3) return "Title must be at least 3 characters";
        break;
      case 'description':
        if (!value?.trim()) return "Description is required";
        if (value.length > 200) return "Description must be ≤ 200 characters";
        if (!VALID_TEXT_REGEX.test(value)) return "Description contains invalid characters";
        if (value.trim().length < 5) return "Description must be at least 5 characters";
        break;
      case 'remark':
        if (value && value.length > 1000) return "Remark must be ≤ 1000 characters";
        break;
      case 'employee':
        if (!value) return "Employee is required";
        break;
      case 'manager':
        if (!value) return "Manager is required";
        break;
      case 'cycle':
        if (!value) return "Cycle is required";
        break;
      case 'startDate':
        if (!value) return "Start date is required";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!newGoal.id && new Date(value) < today) return "Start date cannot be in the past";
        break;
      case 'endDate':
        if (!value) return "End date is required";
        if (newGoal.startDate && new Date(value) < new Date(newGoal.startDate)) return "End date cannot be before start date";
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setCreateErrors((prev) => ({ ...prev, [name]: error }));
  };

  const calculateStatus = (progress, endDate) => {
    if (progress === 100) {
      return 'completed';
    }

    if (progress > 0) {
      return 'in_progress';
    }

    return 'not_started';
  };

  const truncate = (text, limit) => {
    if (!text) return "";
    const str = String(text);
    return str.length > limit ? `${str.slice(0, limit)}…` : str;
  };
  const DISPLAY_TRUNCATE_LIMIT = 50; // Same visual width for title and description in list

  // removed seeding helper per request

  const getStatusColor = (status) => {
    switch (status) {
      case "not_started":
        return "bg-gray-100 text-gray-800";
      case "in_progress":
        return "bg-brand/10 text-brand";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "not_started":
        return <Clock className="h-4 w-4" />;
      case "in_progress":
        return <TrendingUp className="h-4 w-4" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleCreateGoal = () => {
    setNewGoal(initialNewGoal);
    setShowCreateModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    const isEditMode = !!newGoal.id;

    try {
      // Client-side validation
      const errors = {};
      const fieldsToValidate = ['title', 'description', 'remark', 'employee', 'manager', 'cycle', 'startDate', 'endDate'];

      fieldsToValidate.forEach(field => {
        const error = validateField(field, newGoal[field]);
        if (error) {
          errors[field] = error;
        }
      });

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors);
        // Mark all fields as touched
        const allTouched = fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {});
        setTouched(allTouched);
        toast.error("Please fix the highlighted errors");
        return;
      }

      // Check for required fields explicitly
      if (!newGoal.title?.trim() || !newGoal.description?.trim() || !newGoal.employee || !newGoal.manager || !newGoal.cycle || !newGoal.startDate || !newGoal.endDate) {
        toast.error('Please fill all required fields.');
        return;
      }

      // Validate selected cycle exists (avoid 400: cycle not found)
      const selectedCycleExists = (Array.isArray(cycles) ? cycles : []).some(
        (c) => c.id === newGoal.cycle
      );
      if (!selectedCycleExists) {
        toast.error("Selected cycle not found. Please re-select a valid cycle.");
        return;
      }
      // Prepare payload per backend schema
      // Find employee to get email, department, and employeeId
      const employeeObj = (Array.isArray(employees) ? employees : []).find(
        (e) => {
          const firstName = e.personalInfo?.firstName || e.user?.firstName || e.firstName || "";
          const lastName = e.personalInfo?.lastName || e.user?.lastName || e.lastName || "";
          const name = e.name || e.fullName || `${firstName} ${lastName}`.trim();
          return name === newGoal.employee;
        }
      );

      // Find manager to get email and managerId
      const managerObj = (Array.isArray(managers) ? managers : []).find(
        (m) => {
          const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
          const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
          const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
          return name === newGoal.manager;
        }
      );

      // Validate that employee and manager are not the same person
      const employeeId = employeeObj?.employeeId || employeeObj?._id || employeeObj?.id;
      const managerId = managerObj?.employeeId || managerObj?._id || managerObj?.id;

      if (employeeId && managerId && employeeId === managerId) {
        toast.error("Employee and Manager cannot be the same person");
        setCreateErrors({ ...createErrors, employee: "Employee and Manager must be different", manager: "Employee and Manager must be different" });
        return;
      }

      // Recalculate status before submission to ensure accuracy
      const calculatedStatus = calculateStatus(Number(newGoal.progress || 0), newGoal.endDate);

      const payload = {
        title: newGoal.title,
        description: newGoal.description || "",
        cycle: newGoal.cycle,
        employee: newGoal.employee,
        employeeId: employeeObj?.employeeId || employeeObj?._id || employeeObj?.id || newGoal.employeeId,
        employeeEmail: employeeObj?.email || employeeObj?.contactInfo?.personalEmail || employeeObj?.user?.email || "",
        employeeDepartment: employeeObj?.department || employeeObj?.employmentInfo?.department || "",
        manager: newGoal.manager,
        managerId: managerObj?.employeeId || managerObj?._id || managerObj?.id || newGoal.managerId,
        managerEmail: managerObj?.email || managerObj?.contactInfo?.personalEmail || managerObj?.user?.email || "",
        status: calculatedStatus,
        priority: newGoal.priority || "medium",
        progress: Number(newGoal.progress || 0),
        target: newGoal.target || "0",
        current: newGoal.current || "0",
        startDate: newGoal.startDate,
        endDate: newGoal.endDate,
        remark: (newGoal.remark || "").trim(),
      };
      if (isEditMode) {
        // Update existing goal
        await updateGoalMutation.mutateAsync({ goalId: newGoal.id, goalData: payload });
      } else {
        // Create new goal
        const result = await createGoalMutation.mutateAsync(payload);

      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} goal:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} goal. Please try again.`);
    }
  };

  const handleViewGoal = (goalId) => {
    if (isDrawer) {
      const goal = goals.find((g) => g.id === goalId || g._id === goalId);
      if (goal) {
        setSelectedGoal(goal);
        setShowViewModal(true);
      }
    } else {
      navigate(`/goal_detail/${goalId}`);
    }
  };

  const handleEditGoal = (goalId) => {
    const goal = goals.find((g) => g.id === goalId || g._id === goalId);
    if (goal) {
      const transformedGoal = {
        ...goal,
        remark: goal.remark || "",
        startDate: goal.startDate ? goal.startDate.split("T")[0] : "",
        endDate: goal.endDate ? goal.endDate.split("T")[0] : "",
      };
      setNewGoal(transformedGoal);
      setShowCreateModal(true);
    }
  };

  const handleDeleteGoal = (goalId) => {
    const goal = goals.find((g) => g.id === goalId);
    setIsBulkDelete(false);
    setGoalToDelete(goal);
    setShowDeleteWarning(true);
  };

  const handleBulkDelete = () => {
    if (selectedGoalsMap.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteWarning(true);
  };

  const confirmDeleteGoal = async () => {
    if (!goalToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedGoalsMap.keys());
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteGoalMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
            console.error(`Error deleting goal ${id}:`, error);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} goal(s) deleted successfully`);
          setSelectedGoalsMap(new Map());
        }
        if (failCount > 0) {
          toast.error(`${failCount} goal(s) could not be deleted`);
        }
      } else {
        await deleteGoalMutation.mutateAsync(goalToDelete.id);
        toast.success("Goal deleted successfully!");
      }
      setShowDeleteWarning(false);
      setGoalToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      console.error("Error deleting goal:", error);
    }
  };

  const cancelDeleteGoal = () => {
    setShowDeleteWarning(false);
    setGoalToDelete(null);
    setIsBulkDelete(false);
  };

  // Determine checkbox states
  const currentPageIds = filteredGoals.map(g => g.id);
  const selectedPageIds = currentPageIds.filter(id => selectedGoalsMap.has(id));
  const isAllSelected = currentPageIds.length > 0 && selectedPageIds.length === currentPageIds.length;
  const isIndeterminate = selectedPageIds.length > 0 && selectedPageIds.length < currentPageIds.length;

  const handleSelectAll = () => {
    // If ANY item is selected globally (not just on current page), clear ALL selections
    // Otherwise, select all on current page (adding to whatever might theoretically exist, but "Select All" clears, so technically it starts fresh or appends? User said "deselect all... when uncheck". If checking, we select current page.)

    // Wait, let's re-read user requirement carefully: "when i uncheck box from table header then it deselect all the rows, do not keep any selection record"
    // Does checking it select all rows globally? Or just current page? Typically checking selects current page.

    // Logic:
    // If currently checked/indeterminate (meaning something is selected): DESELECT EVERYTHING (Global Reset)
    // If currently empty: SELECT CURRENT PAGE

    if (selectedPageIds.length > 0) {
      // Uncheck action -> Clear EVERYTHING
      setSelectedGoalsMap(new Map());
    } else {
      // Check action -> Select all on current page
      // Should we keep existing selections from other pages? Yes, usually "Select" adds.
      const newSelectedMap = new Map(selectedGoalsMap);
      filteredGoals.forEach((goal) => {
        newSelectedMap.set(goal.id, goal);
      });
      setSelectedGoalsMap(newSelectedMap);
    }
  };

  const handleSelectRow = (goal) => {
    const newSelectedMap = new Map(selectedGoalsMap);
    if (newSelectedMap.has(goal.id)) {
      newSelectedMap.delete(goal.id);
    } else {
      newSelectedMap.set(goal.id, goal);
    }
    setSelectedGoalsMap(newSelectedMap);
  };



  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // --- Backend-driven Export Logic ---

  // Base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      q: debouncedSearchTerm?.trim() || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      cycle: filterCycle !== 'all' ? filterCycle : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
    };
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });
    return apiFilters;
  }, [debouncedSearchTerm, filterStatus, filterCycle, filterStartDate, filterEndDate]);

  // Get current page goal IDs
  const currentPageGoalIds = useMemo(
    () =>
      filteredGoals
        .map((goal) => {
          const raw = goal?.id || goal?._id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredGoals]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageGoalIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'performance-goals',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? limit,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `performance-goals-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, limit, currentPageGoalIds]
  );

  // Handle export for selected goals with format selection
  const handleGoalsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedGoalsMap.keys()).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected goals to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedGoalsMap, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleGoalsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              createdAtStart: startDate,
              createdAtEnd: endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageGoalIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, requestBackendExport, limit, currentPageGoalIds]
  );

  const handleExportClick = () => {
    if (selectedGoalsMap.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading goals...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load goals
          </h3>
          <p className="text-red-700 mb-4">
            {error?.message || "An error occurred while loading data"}
          </p>
          <PermissionGate permission="performance" action="read">
            <Button
              variant="primary"
              onClick={() => refetch()}
              className="bg-red-600 hover:bg-red-700"
            >
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }



  // Cycles options for filter
  const cycleOptions = Array.isArray(cycles)
    ? cycles.map((c) => ({ label: c.cycleName, value: c.id }))
    : [];

  // Reusable searchable select (small card within form)
  const SearchSelect = ({ label, value, onChange, options, placeholder = "Search or select", error = false, helperText, showDetails = false, disabled = false, onBlur, name }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          if (open) {
            setOpen(false);
            setQuery("");
            if (onBlur && name) {
              onBlur({ target: { name, value } });
            }
          }
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open, onBlur, name, value]);

    const normalized = Array.isArray(options)
      ? options.map((opt) => {
        if (typeof opt === 'string') {
          return { label: opt, displayLabel: opt };
        }
        const mainLabel = opt.label || opt.name || opt.fullName || "";
        const employeeId = opt.employeeId || opt.id || opt._id || "";
        const department = opt.department || "";
        const email = opt.email || "";

        return {
          label: mainLabel,
          displayLabel: mainLabel,
          employeeId: employeeId,
          department: department,
          email: email,
          ...opt
        };
      })
      : [];
    const filtered = normalized.filter((o) => {
      const searchText = query.toLowerCase();
      return o.label.toLowerCase().includes(searchText) ||
        (o.employeeId && o.employeeId.toLowerCase().includes(searchText)) ||
        (o.department && o.department.toLowerCase().includes(searchText)) ||
        (o.email && o.email.toLowerCase().includes(searchText));
    });
    return (
      <div className="relative" ref={dropdownRef}>
        {label ? (
          <label className="block text-sm font-medium text-gray-700">{label}</label>
        ) : null}
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          className={`mt-1 w-full border rounded-md p-2 flex items-center justify-between text-left outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${error ? "border-red-500" : "border-gray-300"} ${disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
        >
          <span
            className={`flex-1 min-w-0 mr-3 truncate ${value ? "text-gray-900" : "text-gray-500"}`}
            title={value || placeholder}
          >
            {value || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
        </button>
        {open && !disabled && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and approved symbols
                  const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                  setQuery(value);
                }}
                maxLength={100}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md p-2 text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No results</div>
              )}
              {filtered.map((opt, idx) => (
                <button
                  key={`${opt.label}-${idx}`}
                  type="button"
                  onClick={() => {
                    onChange(opt.label);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-brand/5 text-sm"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.label}</span>
                    {showDetails && (opt.employeeId || opt.department) && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {opt.employeeId && opt.department
                          ? `${opt.employeeId} - ${opt.department}`
                          : opt.employeeId || opt.department}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {helperText && (
          <p className={`mt-1 text-xs ${error ? "text-red-600 flex items-center" : "text-gray-500"}`}>
            {error && <AlertCircle className="h-4 w-4 mr-1" />}
            {helperText}
          </p>
        )}
      </div>
    );
  };

  const renderFormFields = (goalState, setGoalState, isEditMode = false) => (
    <>
      <div className="max-w-[320px] w-full">
        <label className="block text-sm font-medium text-gray-700">
          Cycle <span className="text-red-500">*</span>
        </label>
        <select
          name="cycle"
          value={goalState.cycle || ""}
          onChange={(e) => {
            setGoalState({ ...goalState, cycle: e.target.value });
            if (createErrors.cycle) {
              setCreateErrors((prev) => ({ ...prev, cycle: undefined }));
            }
          }}
          onBlur={handleBlur}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2.5 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all ${touched.cycle && createErrors.cycle ? "border-red-500" : "border-gray-300"}`}
        >
          <option value="">Select a performance cycle</option>
          {Array.isArray(cycles) &&
            cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id} className="capitalize">
                {cycle.cycleName}
              </option>
            ))}
        </select>
        {touched.cycle && createErrors.cycle && (
          <p className="mt-1 text-xs text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {createErrors.cycle}
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={goalState.title || ""}
          onChange={(e) => {
            const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 80);
            setGoalState({ ...goalState, title: value });
            if (createErrors.title) {
              setCreateErrors((prev) => ({ ...prev, title: undefined }));
            }
          }}
          onBlur={handleBlur}
          maxLength={80}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2.5 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all ${touched.title && createErrors.title ? "border-red-500" : "border-gray-300"}`}
        />
        <div className="flex justify-between items-center mt-1">
          {touched.title && createErrors.title && (
            <p className="text-xs text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {createErrors.title}
            </p>
          )}
          <p className="text-xs text-gray-500 ml-auto">
            {(goalState.title || "").length}/80 characters
          </p>
        </div>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          name="description"
          value={goalState.description || ""}
          onChange={(e) => {
            const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 200);
            setGoalState({ ...goalState, description: value });
            if (createErrors.description) {
              setCreateErrors((prev) => ({ ...prev, description: undefined }));
            }
          }}
          onBlur={handleBlur}
          maxLength={200}
          rows={3}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2 resize-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.description && createErrors.description ? "border-red-500" : "border-gray-300"}`}
        />
        <div className="flex justify-between items-center mt-1">
          {touched.description && createErrors.description && (
            <p className="text-xs text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {createErrors.description}
            </p>
          )}
          <p className="text-xs text-gray-500 ml-auto">
            {(goalState.description || "").length}/200 characters
          </p>
        </div>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700">Remark</label>
        <textarea
          name="remark"
          value={goalState.remark || ""}
          onChange={(e) => {
            const value = e.target.value.slice(0, 1000);
            setGoalState({ ...goalState, remark: value });
            if (createErrors.remark) setCreateErrors({ ...createErrors, remark: undefined });
          }}
          onBlur={handleBlur}
          maxLength={3000}
          rows={5}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2 resize-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.remark && createErrors.remark ? "border-red-500" : "border-gray-300"}`}
          placeholder="Add remarks for the employee (optional)"
        />
        <div className="flex justify-between items-center mt-1">
          {touched.remark && createErrors.remark && (
            <p className="text-xs text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {createErrors.remark}
            </p>
          )}
          <p className="text-xs text-gray-500 ml-auto">
            {(goalState.remark || "").length}/3000 characters
          </p>
        </div>
      </div>

      <SearchSelect
        label={
          <>
            Employee <span className="text-red-500">*</span>
          </>
        }
        name="employee"
        value={goalState.employee || ""}
        disabled={isEditMode}
        onBlur={handleBlur}
        onChange={(val) => {
          const match = (Array.isArray(employees) ? employees : []).find(
            (e) => {
              const firstName = e.personalInfo?.firstName || e.user?.firstName || e.firstName || "";
              const lastName = e.personalInfo?.lastName || e.user?.lastName || e.lastName || "";
              const name = e.name || e.fullName || `${firstName} ${lastName}`.trim();
              return name === val;
            }
          );
          const matchId = match?.employeeId || match?._id || match?.id;
          const managerMatch = (Array.isArray(managers) ? managers : []).find(
            (m) => {
              const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
              const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
              const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
              return name === goalState.manager;
            }
          );
          const managerMatchId = managerMatch?.employeeId || managerMatch?._id || managerMatch?.id;

          // If selected employee is the same as manager, clear manager
          const updatedState = { ...goalState, employee: val, employeeId: matchId };
          if (matchId && managerMatchId && matchId === managerMatchId) {
            updatedState.manager = "";
            updatedState.managerId = "";
            toast.error("Employee and Manager cannot be the same person");
          }
          setGoalState(updatedState);
          // Manually handle validation success
          if (val) {
            setCreateErrors((prev) => ({ ...prev, employee: undefined }));
          }
        }}
        options={Array.isArray(employees) ? employees
          .filter((e) => {
            // Filter out the selected manager from employee options
            if (!goalState.manager) return true;
            const managerMatch = (Array.isArray(managers) ? managers : []).find(
              (m) => {
                const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
                const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
                const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
                return name === goalState.manager;
              }
            );
            const managerMatchId = managerMatch?.employeeId || managerMatch?._id || managerMatch?.id;
            const employeeId = e.employeeId || e._id || e.id;
            return employeeId !== managerMatchId;
          })
          .map((e) => {
            const firstName = e.personalInfo?.firstName || e.user?.firstName || e.firstName || "";
            const lastName = e.personalInfo?.lastName || e.user?.lastName || e.lastName || "";
            const name = e.name || e.fullName || `${firstName} ${lastName}`.trim();
            return {
              label: name,
              employeeId: e.employeeId || e.id || e._id || "",
              department: e.department || e.employmentInfo?.department || "",
              email: e.email || e.contactInfo?.personalEmail || e.user?.email || "",
              value: e._id || e.id
            };
          }) : []}
        placeholder="Search or select employee"
        error={!!createErrors.employee}
        helperText={createErrors.employee}
        showDetails={true}
      />
      <SearchSelect
        label={
          <>
            Manager <span className="text-red-500">*</span>
          </>
        }
        name="manager"
        value={goalState.manager || ""}
        onBlur={handleBlur}
        onChange={(val) => {
          const match = (Array.isArray(managers) ? managers : []).find(
            (m) => {
              const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
              const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
              const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
              return name === val;
            }
          );
          const matchId = match?.employeeId || match?._id || match?.id;
          const employeeMatch = (Array.isArray(employees) ? employees : []).find(
            (e) => {
              const firstName = e.personalInfo?.firstName || e.user?.firstName || e.firstName || "";
              const lastName = e.personalInfo?.lastName || e.user?.lastName || e.lastName || "";
              const name = e.name || e.fullName || `${firstName} ${lastName}`.trim();
              return name === goalState.employee;
            }
          );
          const employeeMatchId = employeeMatch?.employeeId || employeeMatch?._id || employeeMatch?.id;

          // If selected manager is the same as employee, clear employee
          const updatedState = { ...goalState, manager: val, managerId: matchId };
          if (matchId && employeeMatchId && matchId === employeeMatchId) {
            updatedState.employee = "";
            updatedState.employeeId = "";
            toast.error("Employee and Manager cannot be the same person");
          }
          setGoalState(updatedState);
          if (createErrors.manager) {
            setCreateErrors((prev) => ({ ...prev, manager: undefined }));
          }
        }}
        options={Array.isArray(managers) ? managers
          .filter((m) => {
            // Filter out the selected employee from manager options
            if (!goalState.employee) return true;
            const employeeMatch = (Array.isArray(employees) ? employees : []).find(
              (e) => {
                const firstName = e.personalInfo?.firstName || e.user?.firstName || e.firstName || "";
                const lastName = e.personalInfo?.lastName || e.user?.lastName || e.lastName || "";
                const name = e.name || e.fullName || `${firstName} ${lastName}`.trim();
                return name === goalState.employee;
              }
            );
            const employeeMatchId = employeeMatch?.employeeId || employeeMatch?._id || employeeMatch?.id;
            const managerId = m.employeeId || m._id || m.id;
            return managerId !== employeeMatchId;
          })
          .map((m) => {
            const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
            const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
            const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
            return {
              label: name,
              employeeId: m.employeeId || m.id || m._id || "",
              department: m.department || m.employmentInfo?.department || "",
              email: m.email || m.contactInfo?.personalEmail || m.user?.email || "",
              value: m._id || m.id
            };
          }) : []}
        placeholder="Search or select manager"
        error={!!createErrors.manager}
        helperText={createErrors.manager}
        showDetails={true}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700">Status (Auto change based on progress)</label>
        <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2.5 bg-gray-50">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${goalState.status === 'completed' ? 'bg-green-100 text-green-800' :
            goalState.status === 'in_progress' ? 'bg-brand/10 text-brand' :
              'bg-gray-100 text-gray-800'
            }`}>
            {goalState.status === 'completed' ? '✓' :
              goalState.status === 'in_progress' ? '→' : '○'}
            <span className="ml-1 capitalize">{goalState.status?.replace('_', ' ') || 'Not Started'}</span>
          </span>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Priority</label>
        <select
          value={goalState.priority || "medium"}
          onChange={(e) => setGoalState({ ...goalState, priority: e.target.value })}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2.5 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Start Date <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="date"
            name="startDate"
            value={goalState.startDate || ""}
            onChange={(e) => {
              if (!isEditMode) {
                setGoalState({ ...goalState, startDate: e.target.value });
                if (createErrors.startDate) {
                  setCreateErrors((prev) => ({ ...prev, startDate: undefined }));
                }
              }
            }}
            onBlur={handleBlur}
            onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
            className={`mt-1 block w-full border rounded-md shadow-sm p-2.5 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all ${touched.startDate && createErrors.startDate ? "border-red-500" : "border-gray-300"} ${isEditMode ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
            min={isEditMode ? undefined : new Date().toISOString().split("T")[0]}
            disabled={isEditMode}
            title={isEditMode ? "Start date cannot be edited" : undefined}
          />
        </div>
        {touched.startDate && createErrors.startDate && (
          <p className="mt-1 text-xs text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {createErrors.startDate}
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          End Date <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="date"
            name="endDate"
            value={goalState.endDate || ""}
            onChange={(e) => {
              const newEndDate = e.target.value;
              // Recalculate status when endDate changes
              const calculatedStatus = calculateStatus(goalState.progress || 0, newEndDate);
              setGoalState({ ...goalState, endDate: newEndDate, status: calculatedStatus });
              if (createErrors.endDate) {
                setCreateErrors((prev) => ({ ...prev, endDate: undefined }));
              }
            }}
            onBlur={handleBlur}
            onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
            className={`mt-1 block w-full border rounded-md shadow-sm p-2.5 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all ${touched.endDate && createErrors.endDate ? "border-red-500" : "border-gray-300"}`}
            min={goalState.startDate || new Date().toISOString().split("T")[0]}
          />
        </div>
        {touched.endDate && createErrors.endDate && (
          <p className="mt-1 text-xs text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {createErrors.endDate}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Progress</label>
        <div className="mt-1 flex items-center space-x-3">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Number.isFinite(goalState.progress) ? goalState.progress : 0}
            onChange={(e) => {
              const v = Math.max(0, Math.min(100, Number(e.target.value || 0)));
              // Automatically calculate status based on progress and endDate
              const calculatedStatus = calculateStatus(v, goalState.endDate);
              setGoalState({ ...goalState, progress: v, status: calculatedStatus });
            }}
            className="w-full"
          />
          <span className="w-10 text-right text-sm text-gray-700">{Number(goalState.progress || 0)}%</span>
        </div>
        {createErrors.progress && (
          <p className="mt-1 text-xs text-red-600">{createErrors.progress}</p>
        )}
      </div>

    </>
  );

  const renderViewFields = (goal) => {

    return (
      <div className="space-y-6">
        {/* Title Section */}
        <div className="bg-brand/5 rounded-lg p-4 border border-brand/20">
          <h3 className="text-xl font-bold text-gray-900 break-words">{goal.title || "N/A"}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
              {getStatusIcon(goal.status)}
              <span className="ml-1 capitalize">
                {goal.status ? goal.status.replace("_", " ") : "N/A"}
              </span>
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(goal.priority)}`}>
              {goal.priority || "N/A"}
            </span>

          </div>
        </div>

        {/* Main Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee
            </label>
            <div className="space-y-1 break-words">
              <p className="text-sm font-medium text-gray-900 break-words">{goal.employee || "N/A"}</p>
              {goal.employeeId && (
                <p className="text-xs text-gray-500 mt-0.5 break-words">ID: {goal.employeeId}</p>
              )}
              {goal.employeeEmail && (
                <p className="text-xs text-gray-500 mt-0.5 break-words">{goal.employeeEmail}</p>
              )}
              {goal.employeeDepartment && (
                <p className="text-xs text-gray-500 mt-0.5 break-words">Dept: {goal.employeeDepartment}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager
            </label>
            <div className="space-y-1 break-words">
              <p className="text-sm font-medium text-gray-900 break-words">{goal.manager || "N/A"}</p>
              {goal.managerId && (
                <p className="text-xs text-gray-500 mt-0.5 break-words">ID: {goal.managerId}</p>
              )}
              {goal.managerEmail && (
                <p className="text-xs text-gray-500 mt-0.5 break-words">{goal.managerEmail}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <p className="text-sm text-gray-900">
              {goal.startDate ? formatDate(goal.startDate, { includeTime: false }) : "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <p className="text-sm text-gray-900">
              {goal.endDate ? formatDate(goal.endDate, { includeTime: false }) : "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Progress
            </label>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-brand h-2.5 rounded-full transition-all"
                  style={{ width: `${Number(goal.progress || 0)}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900 min-w-[40px] text-right">{Number(goal.progress || 0)}%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cycle
            </label>
            <p className="text-sm text-gray-900 break-words">
              {Array.isArray(cycles) && cycles.length > 0
                ? cycles.find((cycle) => cycle.id === goal.cycle)?.cycleName ||
                goal.cycle ||
                "N/A"
                : goal.cycle || "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Updated
            </label>
            <p className="text-sm text-gray-900 break-words">
              {goal.lastUpdated
                ? formatDate(goal.lastUpdated, { includeTime: false })
                : goal.updatedAt
                  ? formatDate(goal.updatedAt, { includeTime: false })
                  : "N/A"}
            </p>
          </div>
          {goal.remark && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark
              </label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 border border-gray-200">
                {goal.remark}
              </p>
            </div>
          )}
          {goal.status === 'completed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Completed Date
              </label>
              <p className="text-sm text-gray-900">
                {goal.completedAt
                  ? formatDate(goal.completedAt, { includeTime: false })
                  : goal.updatedAt
                    ? formatDate(goal.updatedAt, { includeTime: false })
                    : "N/A"}
              </p>
            </div>
          )}
        </div>

        {/* Description Section - Moved to Bottom */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
            {goal.description || "N/A"}
          </p>
        </div>
      </div>
    );
  };

  if (isDrawer) {
    if (initialMode === 'create' || initialMode === 'edit') {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmitCreate} id="create-goal-form">
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-1 h-5 bg-brand rounded-full mr-2"></div>
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {renderFormFields(newGoal, setNewGoal, !!newGoal?.id)}
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-3">
            <Button variant="primary" onClick={closeModals} className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">Cancel</Button>
            <Button variant="primary" type="submit" form="create-goal-form" className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">
              {newGoal.id ? "Update Goal" : "Create Goal"}
            </Button>
          </div>
        </div>
      );
    }
    if (initialMode === 'view' && selectedGoal) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {renderViewFields(selectedGoal)}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
            <Button variant="primary" onClick={closeModals} className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">Close</Button>
          </div>
        </div>
      );
    }
    return <div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goal Management</h1>
          <p className="text-gray-600">
            Manage employee goals, KRAs, and performance objectives
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedGoalsMap.size > 0 && (
            <PermissionGate permission="performance" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedGoalsMap.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="performance" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedGoalsMap.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedGoalsMap.size > 0
                    ? `Export ${selectedGoalsMap.size} selected goals`
                    : 'Export goals'
                }
              >
                {selectedGoalsMap.size > 0 ? `Export Selected (${selectedGoalsMap.size})` : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedGoalsMap.size > 0 && showInlineSelectedFormats}
                disabled={exportLoading}
                onSelect={handleGoalsSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          {/* Seed button removed as per request */}
          <PermissionGate permission="performance" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateGoal}
              className="bg-brand hover:bg-brand-dark text-white border-transparent"
            >
              Create Goal
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPIs */}
      {(() => {
        const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
            let bgColor = "bg-[#0F2942]";
            if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
            else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
            else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
            else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
            else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

            return (
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
                    <div className="space-y-4 flex-1">
                        <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                            {title}
                        </h3>
                        <div className="flex items-baseline space-x-2">
                            <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                                {value}
                            </span>
                            {(subtitle || trend) && (
                                <div className="flex items-center space-x-2">
                                    {subtitle && (
                                        <span className="text-sm font-medium text-slate-500">
                                            {subtitle}
                                        </span>
                                    )}
                                    {trend && (
                                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                            {trend.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
            );
        };

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Target}
              title="Total Goals"
              value={renderStatValue(normalizedGoalStats.totalGoals)}
              color="bg-brand"
            />
            <KpiCard
              icon={TrendingUp}
              title="In Progress"
              value={renderStatValue(normalizedGoalStats.inProgress)}
              color="bg-yellow-500"
            />
            <KpiCard
              icon={CheckCircle}
              title="Completed"
              value={renderStatValue(normalizedGoalStats.completed)}
              color="bg-green-600"
            />
            <KpiCard
              icon={BarChart3}
              title="Avg Progress"
              value={renderStatValue(normalizedGoalStats.avgProgress, "%")}
              color="bg-purple-600"
            />
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Goal or Employee ..."
              ref={searchInputRef}
              value={searchInput}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and approved symbols
                const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                setSearchInput(value);
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
              autoFocus
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="flex-shrink-0 min-w-[240px] max-w-[320px] w-full">
            <SearchSelect
              label=""
              value={
                filterCycle === "all"
                  ? ""
                  : (cycleOptions.find((o) => o.value === filterCycle)?.label || "")
              }
              onChange={(val) => {
                if (!val || val === "All Cycles") return setFilterCycle("all");
                const match = cycleOptions.find((o) => o.label === val);
                setFilterCycle(match?.value || "all");
              }}
              options={[{ label: "All Cycles" }, ...cycleOptions]}
              placeholder="All Cycles"
            />
          </div>
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={({ startDate, endDate }) => {
                updateTableFilter({ startDate, endDate });
              }}
              inputClassName="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-brand"
            />
          </div>
          {(searchInput || filterStatus !== 'all' || filterCycle !== 'all' || filterStartDate || filterEndDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  resetAllFilters();
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Goals List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
              <tr>
                <th className="px-6 py-3 text-left max-w-[50px]">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                      checked={isAllSelected}
                      ref={input => { if (input) input.indeterminate = isIndeterminate; }}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Goal Details</span>
                    {sortField === 'title' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('employee')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortField === 'employee' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('progress')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Progress</span>
                    {sortField === 'progress' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Priority</span>
                    {sortField === 'priority' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                  onClick={() => handleSort('endDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Due Date</span>
                    {sortField === 'endDate' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created At</span>
                    {sortField === 'createdAt' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGoals.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <FileText className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No goals found</h3>
                      {(searchInput || filterStatus !== 'all' || filterCycle !== 'all') && (
                        <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                      )}
                      {!searchInput && filterStatus === 'all' && filterCycle === 'all' && (
                        <p className="text-sm text-gray-500">Get started by creating your first goal.</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                sortedGoals.map((goal) => (
                  <tr key={goal.id} className={`hover:bg-blue-50/30 ${selectedGoalsMap.has(goal.id) ? 'bg-brand/5' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                          checked={selectedGoalsMap.has(goal.id)}
                          onChange={() => handleSelectRow(goal)}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900" title={goal.title}>
                          {truncate(goal.title, DISPLAY_TRUNCATE_LIMIT)}
                        </div>
                        <div className="text-sm text-gray-500" title={goal.description}>
                          {truncate(goal.description, DISPLAY_TRUNCATE_LIMIT)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap max-w-[220px]">
                      <div className="flex flex-col gap-1">
                        <div
                          className="text-sm font-medium text-gray-900 truncate"
                          title={goal.employee}
                        >
                          {goal.employee}
                        </div>
                        <div
                          className="text-xs text-gray-500 truncate"
                          title={`Manager: ${goal.manager}`}
                        >
                          Manager: {goal.manager}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          goal.status
                        )}`}
                      >
                        {getStatusIcon(goal.status)}
                        <span className="ml-1 capitalize">
                          {goal.status.replace("_", " ")}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-brand h-2 rounded-full"
                            style={{ width: `${goal.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-900">
                          {goal.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                          goal.priority
                        )}`}
                      >
                        {goal.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(goal.endDate, { includeTime: false })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(goal.createdAt, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center">
                        <PermissionGate permission="performance" action="read">
                          <button
                            onClick={() => handleViewGoal(goal.id)}
                            className="p-2 text-brand hover:text-brand-dark hover:bg-brand/5 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="performance" action="update">
                          <button
                            onClick={() => handleEditGoal(goal.id)}
                            className="p-2 text-brand hover:text-brand-dark hover:bg-brand/5 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="performance" action="delete">
                          <button
                            onClick={() => handleDeleteGoal(goal.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {goals.length > 0 && (
          <PaginationControls
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            limit={pagination.limit}
            hasNextPage={pagination.page < pagination.totalPages}
            hasPrevPage={pagination.page > 1}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            border={false}
          />
        )}
      </div>

      {/* Goal Categories Overview - hidden as per request */}

      {/* Create Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-brand text-white">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{newGoal.id ? "Edit Goal" : "Create New Goal"}</h2>
                    <p className="text-sm opacity-80 mt-0.5">{newGoal.id ? "Update the performance goal details" : "Set up a new performance goal for your team"}</p>
                  </div>
                </div>
                <button
                  onClick={closeModals}
                  className="text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <form onSubmit={handleSubmitCreate} id="create-goal-form">
                  <div className="space-y-6">
                    {/* Basic Information Section */}
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-1 h-5 bg-brand rounded-full mr-2"></div>
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {renderFormFields(newGoal, setNewGoal, !!newGoal?.id)}
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-3">
                <Button
                  variant="primary"
                  onClick={closeModals}
                  className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
                >
                  Cancel
                </Button>
                <PermissionGate permission={newGoal.id ? "performance" : "performance"} action={newGoal.id ? "update" : "create"}>
                  <Button
                    variant="primary"
                    type="submit"
                    form="create-goal-form"
                    className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
                  >
                    {newGoal.id ? (
                      <>
                        <Edit className="h-4 w-4 mr-2" />
                        Update Goal
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Goal
                      </>
                    )}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* View Modal */}
      {
        showViewModal && selectedGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-brand text-white">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">View Goal</h2>
                    <p className="text-sm opacity-80 mt-0.5">View performance goal details</p>
                  </div>
                </div>
                <button
                  onClick={closeModals}
                  className="text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {renderViewFields(selectedGoal)}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
                <Button
                  variant="primary"
                  onClick={closeModals}
                  className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Warning Modal */}
      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteGoal}
        onConfirm={confirmDeleteGoal}
        title={isBulkDelete ? "Delete Goals" : "Delete Goal"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedGoalsMap.size} selected goal(s)? This action cannot be undone.`
          : `Are you sure you want to delete "${goalToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Goals"
        showDateRange
        scopedFormatExport={{
          onExport: handleGoalsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default GoalManagement;
